import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, MapPin, Mail, Phone, Globe, Loader2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { useOrder, orderMessage } from "@/context/OrderContext";
import { waLink, ADDRESS, EMAIL, PHONE_DISPLAY, MAPS_URL, SOCIALS, SHOPS, WEBSITE, WEBSITE_HOST } from "@/lib/site";
import { useLang } from "@/context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full rounded-2xl border border-leaf/20 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[var(--leaf)]";

const OrderForm = () => {
  const { items, setQty, clear } = useOrder();
  const [f, setF] = useState({ name: "", phone: "", email: "", city: "", order_type: "Retail", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const { t } = useLang();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length && !f.notes.trim()) { toast.error(t("Tambahkan tanaman ke pesanan atau tulis kebutuhan Anda di catatan.", "Add plants to your order or describe what you need in the notes.")); return; }
    setBusy(true);
    try {
      const payload = { ...f, items: items.map((i) => ({ name: i.name, price: i.price || "", qty: i.qty })) };
      const res = await axios.post(`${API}/orders`, payload);
      const msg = orderMessage(items, t(`Nama: ${f.name}\nNo. HP: ${f.phone}${f.city ? `\nKota: ${f.city}` : ""}\nJenis: ${f.order_type}${f.notes ? `\nCatatan: ${f.notes}` : ""}\nNo. Pesanan: ${res.data?.id?.slice(0, 8) ?? "-"}`, `Name: ${f.name}\nPhone: ${f.phone}${f.city ? `\nCity/Country: ${f.city}` : ""}\nType: ${f.order_type}${f.notes ? `\nNotes: ${f.notes}` : ""}\nOrder No.: ${res.data?.id?.slice(0, 8) ?? "-"}`));
      setDone({ id: res.data?.id ?? "", msg });
      clear();
      toast.success(t("Pesanan terkirim! Tim Minaqu akan segera menghubungi Anda.", "Order sent! The Minaqu team will contact you shortly."));
    } catch {
      toast.error(t("Gagal mengirim formulir. Silakan coba lagi atau pesan via WhatsApp.", "Could not send the form. Please try again or order via WhatsApp."));
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <div data-testid="order-success" className="rounded-[2rem] bg-[var(--leaf)] p-8 sm:p-10 text-white">
      <p className="font-display text-3xl font-black">{t("Terima kasih!", "Thank you!")}</p>
      <p className="mt-3 text-white/85">{t(`Pesanan Anda tercatat (No. ${done.id.slice(0, 8)}). Agar lebih cepat diproses, kirim juga ringkasannya ke WhatsApp kami.`, `Your order has been recorded (No. ${done.id.slice(0, 8)}). For faster processing, also send the summary to our WhatsApp.`)}</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <a data-testid="order-success-wa" href={waLink(done.msg)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-display text-sm font-bold text-[var(--leaf-deep)]"><MessageCircle className="h-4 w-4" /> {t("Kirim ke WhatsApp", "Send to WhatsApp")}</a>
        <button data-testid="order-success-new" onClick={() => setDone(null)} className="rounded-full border border-white/50 px-5 py-3 font-display text-sm font-bold">{t("Buat pesanan baru", "New order")}</button>
      </div>
    </div>
  );

  return (
    <form data-testid="order-form" onSubmit={submit} className="rounded-[2rem] bg-white p-6 sm:p-10 ring-1 ring-leaf/10">
      <p className="font-display text-sm font-black uppercase tracking-[0.18em] text-[var(--leaf)]">{t("Daftar pesanan", "Order list")} ({items.length})</p>
      <ul data-testid="order-form-items" className="mt-3 divide-y divide-leaf/10">
        {items.length === 0 && <li className="py-3 text-sm text-[var(--muted-ink)]">{t("Belum ada item. Pilih dari katalog, atau tulis kebutuhan Anda di catatan.", "No items yet. Choose from the catalogue, or describe what you need in the notes.")}</li>}
        {items.map((i) => (
          <li key={i.id} className="flex items-center justify-between gap-3 py-3 text-sm">
            <span className="min-w-0 truncate font-semibold">{i.name} <span className="font-normal text-[var(--muted-ink)]">· {i.price || t("Tanya harga", "Ask for price")}</span></span>
            <input type="number" min={0} value={i.qty} onChange={(e) => setQty(i.id, Number(e.target.value))} data-testid={`order-form-qty-${i.id}`} className="w-16 rounded-full border px-3 py-1 text-center" />
          </li>
        ))}
      </ul>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <input required minLength={2} data-testid="order-form-name" placeholder={t("Nama lengkap *", "Full name *")} value={f.name} onChange={set("name")} className={field} />
        <input required minLength={6} data-testid="order-form-phone" placeholder={t("No. WhatsApp *", "WhatsApp number (with country code) *")} value={f.phone} onChange={set("phone")} className={field} />
        <input type="email" data-testid="order-form-email" placeholder={t("Email (opsional)", "Email (optional)")} value={f.email} onChange={set("email")} className={field} />
        <input data-testid="order-form-city" placeholder={t("Kota / Negara", "City / Country")} value={f.city} onChange={set("city")} className={field} />
        <select data-testid="order-form-type" value={f.order_type} onChange={set("order_type")} className={`${field} sm:col-span-2`}>
          <option value="Retail">Retail</option><option value="Grosir / Wholesale">{t("Grosir / Wholesale", "Wholesale")}</option><option value="Ekspor">{t("Ekspor", "Export")}</option><option value="Souvenir & Hampers">Souvenir & Hampers</option><option value="Landscaping">Landscaping</option>
        </select>
        <textarea data-testid="order-form-notes" rows={4} placeholder={t("Catatan: jumlah, ukuran pot, alamat kirim, kebutuhan acara…", "Notes: quantity, pot size, shipping address / destination country, event needs…")} value={f.notes} onChange={set("notes")} className={`${field} sm:col-span-2`} />
      </div>
      <button data-testid="order-form-submit-button" disabled={busy} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--leaf)] px-6 py-4 font-display text-sm font-bold text-white transition-colors hover:bg-[var(--leaf-deep)] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Kirim Formulir Pesanan", "Send Order Form")}
      </button>
    </form>
  );
};

const Info = () => (
  <div className="space-y-8">
    <div className="space-y-4 text-[var(--ink)]">
      {[[Phone, PHONE_DISPLAY, waLink()], [Mail, EMAIL, `mailto:${EMAIL}`], [Globe, WEBSITE_HOST, WEBSITE], [MapPin, ADDRESS, MAPS_URL]].map(([I, t, h]) => (
        <a key={t} href={h} target="_blank" rel="noreferrer" className="flex gap-4 group"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--mint)] text-[var(--leaf)] transition-colors group-hover:bg-[var(--leaf)] group-hover:text-white"><I className="h-4 w-4" /></span><span className="pt-2 text-sm">{t}</span></a>
      ))}
    </div>
    <div className="flex flex-wrap gap-2">
      {[...SHOPS, ...SOCIALS].map((s) => <a key={s.id} data-testid={`contact-link-${s.id}`} href={s.href} target="_blank" rel="noreferrer" className="rounded-full border border-leaf/25 bg-white px-4 py-2 font-display text-xs font-bold text-[var(--leaf-deep)] hover:bg-[var(--leaf)] hover:text-white">{s.label}</a>)}
    </div>
    <div className="overflow-hidden rounded-[2rem] ring-1 ring-leaf/10">
      <iframe title="Lokasi Minaqu Indonesia" data-testid="contact-map" src="https://www.google.com/maps?q=Jl.+Raya+Pondok+Bitung,+Sukamantri,+Tamansari,+Bogor&output=embed" className="h-72 w-full" loading="lazy" />
    </div>
  </div>
);

export default function Contact() {
  const { hash } = useLocation();
  const { t } = useLang();
  useEffect(() => {
    if (hash !== "#form") return undefined;
    const t = setTimeout(() => document.getElementById("form")?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, [hash]);
  return (
    <>
      <PageHero testid="contact-hero" eyebrow="Contact Us · For order and more information" lines={t(["Pesan,", "tanya, atau", "berkunjung."], ["Order,", "ask, or", "visit us."])} img="/assets/img/contact-plant.jpg" imgAlt={t("Tanaman Scindapsus Minaqu", "Minaqu Scindapsus plant")}
        intro={t("Pesan langsung lewat WhatsApp, isi formulir pemesanan, atau belanja di Shopee & Tokopedia. Kunjungi juga kebun kami di Bogor.", "Order directly via WhatsApp, fill in the order form, or shop on Shopee & Tokopedia. You are also welcome to visit our nursery in Bogor, Indonesia.")} />
      <section id="form" className="bg-[var(--paper)] pb-24 sm:pb-32 scroll-mt-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-7"><OrderForm /></Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}><Info /></Reveal>
        </div>
      </section>
    </>
  );
}
